// components/Search/Pagination.jsx
import React from 'react';
import PropTypes from 'prop-types';
import './Pagination.scss'

const Pagination = ({ 
    totalItems, 
    itemsPerPage, 
    currentPage, 
    onPageChange,
    maxVisiblePages = 5 
}) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 1) return null;

    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <nav className="pagination-container" aria-label="Results pagination">
            <ul className="pagination">
                <li>
                    <button 
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        aria-label="First page"
                        className="pagination-button"
                    >
                        &laquo;
                    </button>
                </li>
                <li>
                    <button 
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        className="pagination-button"
                    >
                        &lsaquo;
                    </button>
                </li>

                {startPage > 1 && (
                    <>
                        <li>
                            <button 
                                onClick={() => onPageChange(1)}
                                className="pagination-button"
                            >
                                1
                            </button>
                        </li>
                        {startPage > 2 && <li className="pagination-ellipsis">...</li>}
                    </>
                )}

                {pageNumbers.map(number => (
                    <li key={number}>
                        <button
                            onClick={() => onPageChange(number)}
                            className={`pagination-button ${currentPage === number ? 'active' : ''}`}
                            aria-current={currentPage === number ? 'page' : undefined}
                        >
                            {number}
                        </button>
                    </li>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <li className="pagination-ellipsis">...</li>}
                        <li>
                            <button 
                                onClick={() => onPageChange(totalPages)}
                                className="pagination-button"
                            >
                                {totalPages}
                            </button>
                        </li>
                    </>
                )}
    
                <li>
                    <button 
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        className="pagination-button"
                    >
                        &rsaquo;
                    </button>
                </li>
                <li>
                    <button 
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Last page"
                        className="pagination-button"
                    >
                        &raquo;
                    </button>
                </li>
            </ul>
        </nav>
    );
};

Pagination.propTypes = {
    totalItems: PropTypes.number.isRequired,
    itemsPerPage: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    maxVisiblePages: PropTypes.number
};

export default Pagination;
